import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';
import { Clock, Trash2, RotateCcw, ChevronDown, ChevronRight, ChevronLeft, Loader2, ExternalLink, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HISTORY_PER_PAGE = 5;

const TranslationHistory = ({ history = [], onReuse, onDelete, loading = false, onRefresh }) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this translation from history?')) return;
    setDeletingId(id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/translation/history/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) { toast.success('Deleted from history'); if (onDelete) onDelete(id); }
      else toast.error('Failed to delete');
    } catch { toast.error('Failed to delete'); }
    finally { setDeletingId(null); }
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const diffMs = Date.now() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getLanguageLabel = (code) => {
    const labels = { 'en':'English','hi':'Hindi','gu':'Gujarati','ta':'Tamil','te':'Telugu','pa':'Punjabi','mr':'Marathi','kn':'Kannada','ml':'Malayalam','bn':'Bengali','or':'Odia','as':'Assamese','ur':'Urdu','de':'German','fr':'French','es':'Spanish' };
    return labels[code] || code?.toUpperCase() || '??';
  };

  const totalPages = Math.ceil(history.length / HISTORY_PER_PAGE);
  const startIndex = (currentPage - 1) * HISTORY_PER_PAGE;
  const endIndex = startIndex + HISTORY_PER_PAGE;
  const paginatedHistory = history.slice(startIndex, endIndex);

  const getPageNumbers = () => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [1];
    if (currentPage > 3) pages.push('...');
    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);
    if (currentPage <= 3) end = Math.min(4, totalPages - 1);
    if (currentPage >= totalPages - 2) start = Math.max(2, totalPages - 3);
    for (let i = start; i <= end; i++) { if (!pages.includes(i)) pages.push(i); }
    if (currentPage < totalPages - 2 && totalPages > 4) pages.push('...');
    if (totalPages > 1 && !pages.includes(totalPages)) pages.push(totalPages);
    return pages;
  };

  return (
    <aside className="bg-white rounded-xl border border-gray-200 shadow-sm sticky top-4" style={{maxHeight: "calc(100vh - 120px)", overflowY: "auto"}} data-testid="translation-history">
      <div className="flex items-center justify-between p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="flex items-center gap-2">
          <Clock size={18} className="text-gray-500" />
          <span className="font-semibold text-gray-900">Translation History</span>
          {history.length > 0 && <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">{history.length}</span>}
        </div>
        <div className="flex items-center gap-2">
          {onRefresh && <button onClick={(e) => { e.stopPropagation(); onRefresh(); }} className="p-1.5 hover:bg-gray-100 rounded-lg"><RefreshCw size={14} className={`text-gray-500 ${loading ? 'animate-spin' : ''}`} /></button>}
          <button className="p-1 text-gray-500">{isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</button>
        </div>
      </div>

      {isExpanded && (
        <div className="p-4">
          <div className="mb-4 pb-3 border-b border-gray-100">
            <button onClick={() => navigate('/activity')} className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium">
              <ExternalLink size={14} />View All Activity
            </button>
          </div>
          {loading ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-400"><Loader2 size={24} className="animate-spin" /><p className="mt-2 text-sm">Loading history...</p></div>
          ) : history.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-400"><Clock size={32} className="text-gray-300 mb-2" /><p className="font-medium text-gray-500">No translation history yet</p><p className="text-xs text-gray-400 mt-1">Your past translations will appear here</p></div>
          ) : (
            <>
              <div className="space-y-3 max-h-[380px] overflow-y-auto" data-testid="history-list">
                {paginatedHistory.map((item) => {
                  const sourceLang = item.source_lang || item.source_language;
                  const targetLang = item.target_lang || item.target_language;
                  const targetText = item.target_text || item.translated_text;
                  return (
                    <div key={item._id || item.id} className="p-3 bg-gray-50 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors" data-testid="history-item">
                      <div className="flex items-center justify-between text-xs mb-2">
                        <span className="font-medium text-gray-700">{getLanguageLabel(sourceLang)} → {getLanguageLabel(targetLang)}</span>
                        <span className="text-gray-400">{formatRelativeTime(item.created_at || item.timestamp)}</span>
                      </div>
                      <div className="mb-2">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Source</p>
                        <div className="text-sm bg-white rounded p-2 max-h-20 overflow-y-auto border border-gray-100 select-text cursor-text" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.source_text || '') }} />
                      </div>
                      <div className="mb-2">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Translation</p>
                        <div className="text-sm bg-amber-50 rounded p-2 max-h-20 overflow-y-auto border border-amber-100 select-text cursor-text" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(targetText || '') }} />
                      </div>
                      <div className="flex items-center gap-2 mt-2 text-xs">
                        {item.word_count && <span className="text-gray-500">{item.word_count} words</span>}
                        {item.is_tm_match ? <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded font-medium">TM Match (FREE)</span> : item.credits_used > 0 ? <span className="text-gray-400">{item.credits_used.toFixed(2)} credits</span> : null}
                      </div>
                      <div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-200">
                        <button onClick={() => onReuse && onReuse({ ...item, source_lang: sourceLang, target_lang: targetLang, target_text: targetText })} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                          <RotateCcw size={12} />Reuse
                        </button>
                        <button onClick={() => handleDelete(item._id || item.id)} disabled={deletingId === (item._id || item.id)} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50">
                          {deletingId === (item._id || item.id) ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {totalPages > 1 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-center gap-1 flex-wrap">
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50">
                      <ChevronLeft size={14} /><span className="hidden sm:inline">Prev</span>
                    </button>
                    <div className="flex items-center gap-1">
                      {getPageNumbers().map((page, idx) => (
                        page === '...' ? <span key={`e${idx}`} className="px-2 text-gray-400 text-xs">…</span> :
                        <button key={page} onClick={() => setCurrentPage(page)} className={`h-7 w-7 text-xs font-medium rounded transition-colors ${currentPage === page ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>{page}</button>
                      ))}
                    </div>
                    <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages} className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50">
                      <span className="hidden sm:inline">Next</span><ChevronRight size={14} />
                    </button>
                  </div>
                  <p className="text-center text-xs text-gray-400 mt-2">Showing {Math.min(startIndex + 1, history.length)}-{Math.min(endIndex, history.length)} of {history.length} translations</p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </aside>
  );
};

export default TranslationHistory;
